import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { Optional } from '../../Common/interfaces';
import { currentExerciseAtom, exercisesAtom, moduleAtom } from './data';
import { locationAtom } from './data/parseURL';
import { addPageNumber } from './data/addPageNumber';
import { ExerciseDto, ExerciseStatus, ModuleDto } from './interfaces';
import { closeApp } from './nativeAPI';

export function useNavigation(): Navigation {
    const resetCurrentExercise = useSetAtom(locationAtom);
    const exercises = useAtomValue(exercisesAtom);
    const module = useAtomValue(moduleAtom);
    const currentExercise = useAtomValue(currentExerciseAtom);

    return useMemo(
        () =>
            new Navigation(
                resetCurrentExercise,
                module,
                exercises,
                currentExercise,
            ),
        [resetCurrentExercise, module, exercises, currentExercise],
    );
}

class Navigation {
    resetCurrentExercise: () => void;
    module: ModuleDto;
    exercises: ExerciseDto[];
    exercise: Optional<ExerciseDto>;

    constructor(
        resetCurrentExercise: () => void,
        module: ModuleDto,
        exercises: ExerciseDto[],
        exercise: Optional<ExerciseDto>,
    ) {
        this.module = module;
        this.exercises = exercises;
        this.exercise = exercise;
        this.resetCurrentExercise = resetCurrentExercise;
    }

    get canCloseExercise() {
        return this.exercise !== null;
    }

    get canNavigateBack() {
        return this.exercise?.CurrentPage !== 1;
    }

    toExercise(exercise: ExerciseDto, pageNumber: number) {
        const url = `/modules/${this.module.Id}/details/${exercise.Code}/${pageNumber}`;
        history.pushState({ steps: (history.state?.steps ?? 0) + 1 }, '', url);
        document
            .getElementById('root')
            ?.scrollIntoView({ behavior: 'instant', block: 'start' });
        this.resetCurrentExercise();
    }

    toCurrentExercise() {
        const next = this.exercises.find((x) => {
            switch (x.Status) {
                case ExerciseStatus.Blocked:
                case ExerciseStatus.Completed:
                    return false;
                case ExerciseStatus.InProgress:
                case ExerciseStatus.Open:
                    return true;
            }
        });

        if (next == null) {
            this.toModule();
        } else {
            this.toExercise(next, next.CurrentPage);
        }
    }

    toNextExercise(currentExercise: ExerciseDto) {
        const index = this.exercises.findIndex(
            (x) => x.Id === currentExercise.Id,
        );
        const next = this.exercises[index + 1];

        if (next == null) {
            this.toModule();
        } else {
            this.toExercise(next, 1);
        }
    }

    back() {
        if (this.exercise == null) {
            closeApp();
        } else {
            history.back();
        }
    }

    toModule() {
        this.exercise = null;

        const steps = history.state?.steps ?? 0;
        if (steps <= 0) {
            return;
        }

        history.go(-steps);
    }

    addPageNumber = addPageNumber;
}
